/**
 * Read a market view — the `/workspace_market/view/:uuid` route.
 *
 * The read-only page: no save flow, no unsaved-changes prompt, and the only one that shows the
 * market view's metadata and entity actions. It's also the destination the rest of the platform links
 * to, so it's what `open_page`'s `market_view_details` resolves to.
 */
import type {
	MarketViewAssetClassAliases,
	MarketViewDetails,
	MarketViewSettings,
	MarketViewType,
} from "@eu.mdotm.api/sphere";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LabelRounded } from "$root/components/LabelRounded/Index";
import { PageHeader } from "$root/components/PageHeader";
import useInvestmentHorizonSetup from "$root/components/Portfolio/common_hooks/useInvestmentHorizonSetup";
import EntityStatus from "$root/functional-areas/acl/EntityStatus";
import { formatDate } from "$root/localization/formatters";
import { ProgressBar } from "@mdotm/shadcn/legacy/components";
import { useTranslation } from "react-i18next";
import {
	MarketViewActionsMenu,
	MarketViewEditor,
	useMarketViewScenarios,
	useMarketViewSummary,
	useMarketViewWorkspaceConfig,
} from "./shared";

const ViewMarketView = ({ uuid }: { uuid: string }): React.JSX.Element => {
	const { t } = useTranslation();
	const crumbs = useCrumbs();
	const investmentHorizonSetup = useInvestmentHorizonSetup();

	const querySummary = useMarketViewSummary(uuid);
	const summary = querySummary.data;
	const { settings, assetClassAliases } = useMarketViewWorkspaceConfig();

	const forecastHorizonTypography = investmentHorizonSetup.getInvestmentHorizonTypographyFromNewApiValue(
		"multiAsset",
		String(summary?.forecastHorizon),
	);

	const type = summary?.marketViewType;

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("MarketViews", uuid),
					...crumbs.marketViewName({ uuid: summary?.uuid, name: summary?.name }),
				]}
				// Unlike the two creation flows, this page has a designed state for a market view that
				// failed to load, so the header renders ahead of the summary rather than behind a loader.
				subTitle={
					<div>
						{querySummary.isError ? (
							<div className="flex items-center">
								<span className="mr-2">{t("STATUS").toUpperCase()}</span>
								<LabelRounded content={{ status: "ERROR", component: "" }} />
							</div>
						) : (
							<MarketViewMetadata
								summary={summary}
								settings={settings}
								forecastHorizonTypography={forecastHorizonTypography}
							/>
						)}
					</div>
				}
				titleAction={
					<div className="flex items-center space-x-2">
						<MarketViewActionsMenu uuid={uuid} summary={summary} />
					</div>
				}
			/>
			{/* The editor is per-type, so it waits for the summary that names the type. */}
			{type === undefined ? (
				<ProgressBar value="indeterminate" />
			) : (
				<ViewMarketViewEditor
					uuid={uuid}
					type={type}
					isCustom={summary?.custom ?? false}
					settings={settings}
					assetClassAliases={assetClassAliases}
				/>
			)}
		</>
	);
};

/**
 * Split out so the scenario hooks run only once the type is known — a hook can't be called
 * conditionally, and there is no scenario list to ask for without a type.
 */
const ViewMarketViewEditor = ({
	uuid,
	type,
	isCustom,
	settings,
	assetClassAliases,
}: {
	uuid: string;
	type: MarketViewType;
	isCustom: boolean;
	settings: MarketViewSettings | undefined;
	assetClassAliases: MarketViewAssetClassAliases | undefined;
}): React.JSX.Element => {
	const scenario = useMarketViewScenarios({ type, isCustom, uuid });

	return (
		<MarketViewEditor
			type={type}
			isCustom={isCustom}
			mode="view"
			scenario={scenario}
			settings={settings}
			assetClassAliases={assetClassAliases}
			showWidgetHeader={false}
			lockScenarioSelection={false}
			hidePositioning
			remapCommentaryToDefaults={false}
		/>
	);
};

const MarketViewMetadata = ({
	summary,
	settings,
	forecastHorizonTypography,
}: {
	summary: MarketViewDetails | undefined;
	settings: MarketViewSettings | undefined;
	forecastHorizonTypography: string | undefined;
}): React.JSX.Element => {
	const { t } = useTranslation();

	return (
		<div className="flex justify-between items-center">
			<div className="flex gap-4 items-center">
				<div>
					<span className="mr-2 ">Type:</span>
					<span className="font-semibold text-[#3F485A]">
						{summary?.custom
							? (settings?.customMarketViewName ?? "-")
							: summary?.marketViewType
								? t(summary.marketViewType)
								: "-"}
					</span>
				</div>
				<div>
					<span className="mr-2 ">Forecast horizon:</span>
					<span className="font-semibold text-[#3F485A]">{forecastHorizonTypography || "-"}</span>
				</div>
				<div>
					<span className="mr-2 ">Creation date:</span>
					<span className="font-semibold text-[#3F485A]">{formatDate(summary?.creationDate)}</span>
				</div>
				<div>
					<span className="mr-2 ">Expiration date:</span>
					<span className="font-semibold text-[#3F485A]">{formatDate(summary?.endDate)}</span>
				</div>
				<EntityStatus
					entity="MARKET_VIEW"
					accessControl={summary?.richAcl}
					entityId={summary?.uuid}
					entityName={summary?.name}
				/>
			</div>
		</div>
	);
};

export default ViewMarketView;
